import { useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";

import { QuickFilterTypes, Table } from "@cofano/cck";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { ProductTag } from "components/products/ProductPicker";
import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import { ProductReviewStatusTag, ProductStatusTag, ProductValidationStatusTag } from "components/products/ProductTags";

import { Fields, hasOptionalFieldPermission, usePermission } from "utils";

import { ProductTypes } from "view/products/utils/product.types";

import { DocumentType } from "__generated__/enums";
import { GetProductsBySearchDocument, GetProductsBySearchQuery } from "__generated__/view/products/graphql/product.queries.generated";
import { DeleteProductDocument } from "__generated__/view/products/graphql/product.mutations.generated";

import { ChildPropsFromPagination } from "types/commons";

type ProductFromQuery = ChildPropsFromPagination<GetProductsBySearchQuery, "productsBySearch">;

interface ProductsTableProps {
	tableId?: TableId;
	supplierId?: number;
	refetchKey?: number;
}

const ProductsTable = ({ tableId = TableId.Products, supplierId, refetchKey }: ProductsTableProps) => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const session = useStoreState(store => store.session);
	const { isAdmin, isMasterdata, isReadOnly, isCompanyUser, isMainUserLimited } = usePermission();
	const canCreateAndEdit = (isAdmin || isMasterdata || isCompanyUser || isMainUserLimited) && !isReadOnly;

	const client = useApolloClient();

	const { tableProps, refetch, refetchAfterDelete } = useBackendTable({
		id: tableId,
		disableParams: Boolean(supplierId),
		defaultFilter: supplierId ? undefined : [ {
			property: "isActive",
			value: true,
		} ],
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetProductsBySearchDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						productSearch: {
							supplier: supplierId ? { supplierId } : undefined,
							partialName: filter.name,
							partialCode: filter.code,
							externalId: filter.externalId,
							categoryName: filter["category.name"],
							casNumber: filter.casNumber,
							description: filter.description,
							properShippingNameNL: filter.properShippingNameNL,
							properShippingNameEN: filter.properShippingNameEN,
							properShippingNameDE: filter.properShippingNameDE,
							properShippingNameFR: filter.properShippingNameFR,
							densityKgM3: filter.densityKgM3,
							idtfNumber: filter["idtfGood.idtfNumber"],
							nstrCode: filter["nstrGood.nstrCode"],
							unNumber: filter["adnGood.unNumber"],
							adnCode: filter["adnAttributes.adnCode"],
							publicationDate: filter["adnAttributes.publicationDate"] ? moment(filter["adnAttributes.publicationDate"]).format("YYYY-MM-DD") : undefined,
							issuedAt: filter.issuedAtDate ? moment(filter.issuedAtDate).format("YYYY-MM-DD") : undefined,
							needsManualReview: filter.needsManualReview,
							isActive: filter.isActive,
							isValidated: filter.isValidated,
						},
					},
				});

				callback({
					content: result.data.productsBySearch?.content ?? [],
					totalResults: result.data.productsBySearch?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	useEffect(() => {
		if (refetchKey !== undefined) {
			refetch();
		}
	}, [ refetchKey ]);

	const [ deleteProduct ] = useMutation(DeleteProductDocument, {
		onCompleted: () => refetchAfterDelete(),
	});

	return (
		<Table
			{...tableProps}
			onRowDoubleClick={(row, event) => {
				const url = `/view/products/${row.id}/details`;

				if (event.ctrlKey) {
					window.open(url);
				} else {
					navigate(url);
				}
			}}
			columns={[ {
				id: "name",
				title: intl.formatMessage({ id: "PRODUCTS_NAME" }),
				sortable: true,
				width: 250,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "code",
				title: intl.formatMessage({ id: "PRODUCTS_CODE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "code"),
			}, {
				id: "externalId",
				title: intl.formatMessage({ id: "EXTERNAL_ID" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "externalId"),
			}, {
				id: "category.name",
				title: intl.formatMessage({ id: "PRODUCT_CATEGORY" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "category"),
			}, {
				id: "properShippingNameNL",
				title: intl.formatMessage({ id: "PRODUCT_ADN_PROPER_SHIPPING_NAME_NL" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "properShippingNameEN",
				title: intl.formatMessage({ id: "PRODUCT_ADN_PROPER_SHIPPING_NAME_EN" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "properShippingNameDE",
				title: intl.formatMessage({ id: "PRODUCT_ADN_PROPER_SHIPPING_NAME_DE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "properShippingNameFR",
				title: intl.formatMessage({ id: "PRODUCT_ADN_PROPER_SHIPPING_NAME_FR" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "description",
				title: intl.formatMessage({ id: "PRODUCTS_DESCRIPTION" }),
				sortable: true,
				width: 600,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "description"),
			}, {
				id: "casNumber",
				title: intl.formatMessage({ id: "PRODUCTS_CAS_NUMBER" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "idtfGood.idtfNumber",
				title: intl.formatMessage({ id: "PRODUCTS_IDTF" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "idtf"),
				render: (row: ProductFromQuery) => row.idtfGood?.idtfNumber ? <ProductTag type={ProductTypes.IDTF} product={row.idtfGood} /> : "",
			}, {
				id: "nstrGood.nstrCode",
				title: intl.formatMessage({ id: "PRODUCTS_NSTR" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "nstr"),
				render: (row: ProductFromQuery) => row.nstrGood?.nstrCode ? <ProductTag type={ProductTypes.NSTR} product={row.nstrGood} /> : "",
			}, {
				id: "adnGood.unNumber",
				title: intl.formatMessage({ id: "PRODUCTS_ADN" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "adn"),
				render: (row: ProductFromQuery) => row.adnGood?.unNumber ? <ProductTag type={ProductTypes.ADN} product={row.adnGood} /> : "",
			}, {
				id: "adnAttributes.adnCode",
				title: intl.formatMessage({ id: "PRODUCT_ADN_ADN_CODE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
			}, {
				id: "relations",
				title: intl.formatMessage({ id: "SUPPLIER" }),
				render: (row: ProductFromQuery) => row.relations?.filter((relation) => relation.supplier).map((relation) => relation.name).join(", ") ?? "",
			}, {
				id: "adnAttributes.publicationDate",
				title: intl.formatMessage({ id: "PRODUCT_ADN_PUBLICATION_DATE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.DATE,
			}, {
				id: "densityKgM3",
				title: intl.formatMessage({ id: "PRODUCTS_DENSITY" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
				disabled: !hasOptionalFieldPermission(session, Fields.PRODUCT, "densityKgM3"),
			}, {
				id: "issuedAtDate",
				title: intl.formatMessage({ id: "PRODUCTS_SDS_ISSUE_DATE" }),
				filterComponent: QuickFilterTypes.DATE,
				sortable: true,
				render: (row: ProductFromQuery) => {
					const activeSDSDocument = row.documents
						?.filter((document) => document.documentType === DocumentType.Sds && document.active && document.issuedAt != null)
						.sort((a, b) => new Date(b.issuedAt!).getDate() - new Date(a.issuedAt!).getDate())[0];
					return activeSDSDocument ? activeSDSDocument.issuedAt : "";
				},
			}, {
				id: "needsManualReview",
				title: intl.formatMessage({ id: "PRODUCTS_NEEDS_MANUAL_REVIEW" }),
				sortable: true,
				filterComponent: QuickFilterTypes.BOOLEAN,
				defaultVisible: true,
				render: (row: ProductFromQuery) => row.needsManualReview ? <ProductReviewStatusTag product={row} /> : "",
			}, {
				id: "isActive",
				title: intl.formatMessage({ id: "PRODUCTS_ACTIVE_STATUS" }),
				sortable: true,
				filterComponent: QuickFilterTypes.BOOLEAN,
				width: 150,
				render: (row: ProductFromQuery) => <ProductStatusTag onChange={() => refetch()} product={row} />,
			}, {
				id: "isValidated",
				title: intl.formatMessage({ id: "PRODUCTS_VALIDATED_STATUS" }),
				sortable: true,
				filterComponent: QuickFilterTypes.BOOLEAN,
				width: 150,
				render: (row: ProductFromQuery) => <ProductValidationStatusTag onChange={() => refetch()} product={row} />,
			} ]}
			rowActions={canCreateAndEdit ? [ {
				icon: <EditIcon />,
				onClick: (row: ProductFromQuery) => navigate(`/view/products/${row.id}/edit`, supplierId ? { state: { relationId: supplierId } } : undefined),
			}, {
				icon: <DeleteIcon />,
				hidden: (row: ProductFromQuery) => Boolean(row.externalId),
				onClick: (row) => {
					confirm({
						type: "DELETE",
					}).then(() => {
						deleteProduct({
							variables: {
								id: row.id,
							},
						});
					});
				},
			} ] : undefined } />

	);
};

export default ProductsTable;
